import type { TssEventEnvelope } from './envelope';

export type FunnelEventHandler = (envelope: TssEventEnvelope) => void;

export interface FunnelEventSubscription {
  unsubscribe: () => void;
}

/**
 * Typed in-browser event bus (ADR 0004 D-bus).
 * One instance per FunnelProvider session; disposed on unmount.
 */
export class FunnelEventBus {
  private readonly target = new EventTarget();
  private disposed = false;

  emit(envelope: TssEventEnvelope): void {
    if (this.disposed) return;
    this.target.dispatchEvent(
      new CustomEvent('funnel:event', { detail: envelope }),
    );
    this.target.dispatchEvent(
      new CustomEvent(`funnel:event:${envelope.name}`, { detail: envelope }),
    );
  }

  on(name: string | '*', handler: FunnelEventHandler): FunnelEventSubscription {
    const eventName =
      name === '*' ? 'funnel:event' : `funnel:event:${name}`;
    const listener = (event: Event) => {
      handler((event as CustomEvent<TssEventEnvelope>).detail);
    };
    this.target.addEventListener(eventName, listener);
    return {
      unsubscribe: () => {
        this.target.removeEventListener(eventName, listener);
      },
    };
  }

  dispose(): void {
    this.disposed = true;
  }

  /**
   * React 18 Strict Mode runs effect cleanup → setup again on the same
   * instance. Cleanup calls `dispose()`; without this, every later `emit`
   * silently no-ops and `[funnel-events]` never appears in the console.
   */
  reactivate(): void {
    this.disposed = false;
  }

  isDisposed(): boolean {
    return this.disposed;
  }
}

export function createFunnelEventBus(): FunnelEventBus {
  return new FunnelEventBus();
}
